import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { X, Save } from "lucide-react";
import { CircularProgress } from "@mui/material";

const timeSchedules = [
  { value: "15 Mins", label: "15 min" },
  { value: "30 Mins", label: "30 min" },
  { value: "45 Mins", label: "45 min" },
  { value: "1 hr", label: "1 hr" },
];
const apiUrl = process.env.REACT_APP_API_URL;

export default function EditEndpointGroupForm({
  groupName,
  id,
  close,
  refresh,
  setRefresh,
  error,
  setError,
}) {
  const [availableEndpoints, setAvailableEndpoints] = useState([]);
  const [groupEndpoints, setGroupEndpoints] = useState([]);
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  const [selectedToRemove, setSelectedToRemove] = useState([]);
  const [timeSchedule, setTimeSchedule] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const accessToken = useSelector((state) => state.accessToken.value);

  useEffect(() => {
    fetchEndpoints();
  }, []);

  const fetchEndpoints = async () => {
    try {
      const queryAvailable = `
        query MyQuery {
          getAvailableEndpoints(GroupName: "default") {
            EndpointName
            id
          }
        }
      `;
      const responseAvailable = await axios.post(
        apiUrl,
        { query: queryAvailable },
        { headers: { Authorization: accessToken } }
      );
      if (
        responseAvailable.data.data &&
        responseAvailable.data.data.getAvailableEndpoints
      )
        setAvailableEndpoints(
          responseAvailable.data.data.getAvailableEndpoints
        );

      const queryGroup = `
        query MyQuery($GroupName: String!) {
          getAvailableEndpoints(GroupName: $GroupName) {
            EndpointName
            id
          }
        }
      `;
      const responseGroup = await axios.post(
        apiUrl,
        {
          query: queryGroup,
          variables: { GroupName: groupName },
        },
        { headers: { Authorization: accessToken } }
      );
      if (
        responseGroup.data.data &&
        responseGroup.data.data.getAvailableEndpoints
      )
        setGroupEndpoints(responseGroup.data.data.getAvailableEndpoints);
      if (
        (responseGroup.data.errors &&
          responseGroup.data.errors[0].errorType == "Unauthorized") ||
        (responseAvailable.data.errors &&
          responseAvailable.data.errors[0].errorType == "Unauthorized")
      ) {
        setError("Unauthorized");
      }
    } catch (error) {
      console.error("Error fetching endpoints:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (selectedToAdd.length > 0) {
        const addQuery = `
          mutation MyMutation($GroupName: String!, $id: String!) {
            updateEndpointGroup(GroupName: $GroupName, id: $id)
          }
        `;
        await Promise.all(
          selectedToAdd.map((endpoint) =>
            axios
              .post(
                apiUrl,
                {
                  query: addQuery,
                  variables: {
                    GroupName: groupName,
                    id: endpoint.id,
                  },
                },
                { headers: { Authorization: accessToken } }
              )
              .then((response) => {
                if (
                  response.data.errors &&
                  response.data.errors[0].errorType == "Unauthorized"
                )
                  setError(
                    "Edit access is not granted to your account for this resource."
                  );
              })
              .catch((error) => {
                console.log(error);
              })
          )
        );
      }
      if (selectedToRemove.length > 0) {
        const removeQuery = `
          mutation MyMutation($GroupName: String!, $id: String!) {
            updateEndpointGroup(GroupName: $GroupName, id: $id)
          }
        `;
        await Promise.all(
          selectedToRemove.map((endpoint) =>
            axios
              .post(
                apiUrl,
                {
                  query: removeQuery,
                  variables: {
                    GroupName: "default",
                    id: endpoint.id,
                  },
                },
                { headers: { Authorization: accessToken } }
              )
              .then((response) => {
                if (
                  response.data.errors &&
                  response.data.errors[0].errorType == "Unauthorized"
                )
                  setError(
                    "Edit access is not granted to your account for this resource."
                  );
              })
              .catch((error) => {
                console.log(error);
              })
          )
        );
      }
      if (timeSchedule) {
        const scheduleQuery = `
          mutation MyMutation($groupName: String!, $timeInterval: String!, $id: String!) {
            updateSchedule(groupName: $groupName, timeInterval: $timeInterval, task: "update", id: $id)
          }
        `;
        await axios
          .post(
            apiUrl,
            {
              query: scheduleQuery,
              variables: {
                groupName: groupName,
                timeInterval: timeSchedule,
                id: id,
              },
            },
            { headers: { Authorization: accessToken } }
          )
          .then((response) => {
            if (
              response.data.errors &&
              response.data.errors[0].errorType == "Unauthorized"
            )
              setError(
                "Edit access is not granted to your account for this resource."
              );
          })
          .catch((error) => {
            console.log(error);
          });
      }

      setRefresh(!refresh);
      close();
    } catch (error) {
      console.error("Error submitting changes:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddEndpointToggle = (endpoint) => {
    setSelectedToAdd((prev) => {
      const isSelected = prev.some((e) => e.id === endpoint.id);
      if (isSelected) {
        return prev.filter((e) => e.id !== endpoint.id);
      } else {
        return [...prev, endpoint];
      }
    });
  };

  const handleRemoveEndpointToggle = (endpoint) => {
    setSelectedToRemove((prev) => {
      const isSelected = prev.some((e) => e.id === endpoint.id);
      if (isSelected) {
        return prev.filter((e) => e.id !== endpoint.id);
      } else {
        return [...prev, endpoint];
      }
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-xl bg-white rounded-lg shadow-xl overflow-hidden">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-[#003366]">
              Manage Endpoints for {groupName}
            </h2>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto">
            {/* Add Endpoints Section */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="block text-lg font-bold">Add Endpoints</label>
                <div className="text-xs text-gray-500">
                  {selectedToAdd.length} selected
                </div>
              </div>
              <div className="border border-gray-300 rounded-md p-2 max-h-48 overflow-y-auto">
                {availableEndpoints.length === 0 ? (
                  <p className="text-sm text-gray-500 p-2">
                    No endpoints available
                  </p>
                ) : (
                  availableEndpoints.map((endpoint) => (
                    <div
                      key={endpoint.id}
                      className="flex items-center py-2 px-1 hover:bg-gray-50"
                    >
                      <input
                        id={`add-endpoint-${endpoint.id}`}
                        type="checkbox"
                        checked={selectedToAdd.some(
                          (e) => e.id === endpoint.id
                        )}
                        onChange={() => handleAddEndpointToggle(endpoint)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`add-endpoint-${endpoint.id}`}
                        className="ml-3 block text-base font-medium text-[#003366] cursor-pointer"
                      >
                        {endpoint.EndpointName}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Remove Endpoints Section */}
            <div className="space-y-3 border-t border-gray-200 pt-4">
              <div className="flex justify-between items-center">
                <label className="block text-lg font-bold">
                  Remove Endpoints
                </label>
                <div className="text-xs text-gray-500">
                  {selectedToRemove.length} selected
                </div>
              </div>
              <div className="border border-gray-300 rounded-md p-2 max-h-48 overflow-y-auto">
                {groupEndpoints.length === 0 ? (
                  <p className="text-sm text-gray-500 p-2">
                    No endpoints in this group
                  </p>
                ) : (
                  groupEndpoints.map((endpoint) => (
                    <div
                      key={endpoint.id}
                      className="flex items-center py-2 px-1 hover:bg-gray-50"
                    >
                      <input
                        id={`remove-endpoint-${endpoint.id}`}
                        type="checkbox"
                        checked={selectedToRemove.some(
                          (e) => e.id === endpoint.id
                        )}
                        onChange={() => handleRemoveEndpointToggle(endpoint)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`remove-endpoint-${endpoint.id}`}
                        className="ml-3 block text-base font-medium text-[#003366] cursor-pointer"
                      >
                        {endpoint.EndpointName}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Time Schedule Radio Buttons */}
            <div className="space-y-3 border-t border-gray-200 pt-4">
              <label className="block text-lg font-bold">Time Schedule</label>
              <div className="grid grid-cols-2 gap-3">
                {timeSchedules.map((schedule) => (
                  <div key={schedule.value} className="flex items-center">
                    <input
                      id={`schedule-${schedule.value}`}
                      type="radio"
                      name="timeSchedule"
                      value={schedule.value}
                      checked={timeSchedule === schedule.value}
                      onChange={() => setTimeSchedule(schedule.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <label
                      htmlFor={`schedule-${schedule.value}`}
                      className="ml-3 block text-base font-medium text-[#003366]"
                    >
                      {schedule.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
            <button
              type="button"
              onClick={close}
              className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-[#003366] rounded hover:bg-[#FFD700]/80"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isSubmitting ||
                (selectedToAdd.length === 0 &&
                  selectedToRemove.length === 0 &&
                  !timeSchedule)
              }
              className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
